// flowCambiarPractica.js — ESPEJO del «Quiero cambiar algo» con HUECOS (5/10/2026, Autoescuela Elices) para las vistas
// previas INTERACTIVAS de Mensajes automáticos (app de autoescuelas y simulador). PURO, sin red.
//
// De dónde sale cada cosa (y __tests__/flowCambiarPractica.mirror.test.js lo compara con el backend):
//   - pantallas M_PRACTICA, M_FECHA, M_CONFIRMAR, M_AVISO        ← aws_lambdas/c_createFlow/flow_mover_practica.json
//   - textos que rellena el endpoint (intro, mensaje, resumen, nota) ← aws_lambdas/c_pedirPracticaFlow/moverFlow.js
//   - horas libres de SU profesor (rejilla, última, pegadas, antelación, alumno, coche, festivos)
//                                                                   ← aws_lambdas/c_pedirPracticaFlow/moverSlots.js
//     (aquí con un horario de EJEMPLO; el test comprueba que el backend saca las mismas horas con él)
//   - el mensaje con el Flow y las confirmaciones al alumno          ← aws_lambdas/c_processMessage/cambioHuecos.js
// Si cambias un texto o una regla en el backend, cámbialo aquí: el test falla hasta que coincidan.
//
// COPIA POR VALOR (fichero idéntico) en apps/conext-ventas/src/simulator/pages/autoescuela/mensajes/
// flowCambiarPractica.js — un test del simulador compara los dos ficheros.

import { addDays, dayLabel, esFestivoNacional, weekday, AHORA_EJEMPLO } from "./flowsPractica";

// El Flow «cambiar práctica» está PUBLICADO (2885703601796961, 5/10/2026, probado en la vista previa de Meta contra el
// endpoint real). Con false, la tarjeta avisaba de que su texto seguía llegando a Peticiones (c_processMessage cae a lo
// de siempre sin flow_id). Va igual AQUÍ y en la copia del simulador.
export const CAMBIO_HUECOS_DISPONIBLE = true;

// ── Pantallas (flow_mover_practica.json) ────────────────────────────────────────────────────────
export const CAMBIAR_UI = {
  M_PRACTICA: { title: "Cambiar práctica", label: "Práctica", footer: "Ver horas libres" },
  M_FECHA: { title: "Elige día y hora", diaLabel: "Día", horaLabel: "Hora", footer: "Continuar" },
  M_CONFIRMAR: { title: "Confirma el cambio", heading: "¿Cambiamos tu práctica?", footer: "Confirmar cambio" },
  M_AVISO: { title: "Cambiar práctica", footer: "Cerrar" },
};

// ── Textos del endpoint (moverFlow.js) y del mensaje (cambioHuecos.js) ──────────────────────────
export const CAMBIO_TEXTOS = {
  introPractica: "¿Qué práctica quieres cambiar? Después eliges una hora libre de tu profesor.",
  nota: {
    mover: "Se cambia al momento y te llega la confirmación por este chat.",
    aprobar: "Se lo pasamos a la autoescuela y te confirma el cambio por este chat.",
  },
  sinHorasTitulo: "Tu profesor no tiene horas libres",
  sinHoras: "En los próximos días no le quedan horas libres para esta práctica. Escríbenos por este chat qué quieres cambiar y la autoescuela te contesta.",
  ningunaTitulo: "Ya no se puede cambiar desde aquí",
  ninguna: (horas) => `Las prácticas de ese mensaje ya no están programadas o empiezan en menos de ${horas} horas. Escríbenos por este chat qué quieres cambiar y la autoescuela te contesta.`,
  // Mensaje con el Flow (cambioHuecos.textoFlow) — pie y botón del mensaje.
  flowVarias: "Elige qué práctica quieres cambiar y una hora libre de tu profesor.",
  flowUna: "Elige una hora libre de tu profesor para tu práctica.",
  flowNota: { mover: "El cambio queda hecho al momento.", aprobar: "La autoescuela te confirma el cambio por aquí." },
  flowPie: "O escríbenos aquí qué quieres cambiar",
  flowCta: "Ver horas libres",
  sinHorasChat: "Ahora mismo tu profesor no tiene horas libres para cambiarla. Escríbenos aquí qué quieres cambiar y la autoescuela te contesta.",
};

/** cambioHuecos.textoFlow */
export function textoFlowCambio({ movibles, al_elegir }) {
  return `${movibles > 1 ? CAMBIO_TEXTOS.flowVarias : CAMBIO_TEXTOS.flowUna} ${CAMBIO_TEXTOS.flowNota[al_elegir === "aprobar" ? "aprobar" : "mover"]}`;
}

/** cambioHuecos.textoConfirmacion */
export function textoConfirmacionCambio({ modo, resumen }) {
  return modo === "aprobar"
    ? `Recibido. Le pasamos a la autoescuela tu cambio:\n\n${resumen}\n\nTe confirmamos por aquí.`
    : `✅ Práctica cambiada\n\n${resumen}\n\nTe mandaremos el recordatorio antes de la clase.`;
}

// ── Reglas (moverSlots.semanaCambioConfig) ──────────────────────────────────────────────────────
export const SEMANA_CAMBIO_DEFAULTS = { modo: "texto", al_elegir: "mover", antelacion_horas: 24, dias_vista: 14 };
export function semanaCambioConfig(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const int = (v, min, max, def) => (Number.isInteger(v) && v >= min && v <= max ? v : def);
  return {
    modo: r.modo === "huecos" ? "huecos" : "texto",
    al_elegir: r.al_elegir === "aprobar" ? "aprobar" : "mover",
    antelacion_horas: int(r.antelacion_horas, 0, 168, SEMANA_CAMBIO_DEFAULTS.antelacion_horas),
    dias_vista: int(r.dias_vista, 1, 60, SEMANA_CAMBIO_DEFAULTS.dias_vista),
  };
}

// ── El EJEMPLO (hora local de la autoescuela) ──────────────────────────────────────────────────
// «Ahora» es el lunes 5 de octubre de 2026 a las 10:24 (como el de la reserva). El profesor trabaja como Óscar en
// Elices: L-V de 9:45 a 14:00 y de 15:00 a 20:45, prácticas de 45 min. El alumno tiene en su semana el martes 6 a
// las 10:30 (Ciudad) y el jueves 8 a las 17:15 (Carretera), las dos con ese profesor y el coche c1; la asignada es
// solo la del martes. El profesor tiene otras prácticas, para que no salga todo libre.
const TURNO = [{ start: "09:45", end: "14:00" }, { start: "15:00", end: "20:45" }];
export const HORARIO_CAMBIO = { lunes: TURNO, martes: TURNO, "miércoles": TURNO, jueves: TURNO, viernes: TURNO };
const DIAS_ES = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const pad = (n) => String(n).padStart(2, "0");
const toMin = (hhmm) => { const [h, m] = String(hhmm).split(":").map(Number); return h * 60 + m; };
const hhmm = (min) => `${pad(Math.floor(min / 60) % 24)}:${pad(min % 60)}`;
const firstName = (s) => String(s || "").trim().split(/\s+/)[0] || "";

/** Prácticas: { practica_id, instructor_id, alumno_id, vehicle_id, ymd, start: "HH:MM", end: "HH:MM", type, status }. */
export function ejemploCambio({ profesor, origen } = {}) {
  const nombre = firstName(profesor) || "Marta";
  const suyas = [
    { practica_id: "pm1", instructor_id: "p0", alumno_id: "al_pablo", vehicle_id: "c1", ymd: "2026-10-06", start: "10:30", end: "11:15", type: "ciudad", status: "programada" },
    { practica_id: "pm2", instructor_id: "p0", alumno_id: "al_pablo", vehicle_id: "c1", ymd: "2026-10-08", start: "17:15", end: "18:00", type: "carretera", status: "programada" },
  ];
  const otras = [];
  // Horas del profesor ya cogidas por otros alumnos (un patrón fijo por día, en su rejilla de 45 min).
  for (let i = 0; i <= 20; i++) {
    const ymd = addDays(AHORA_EJEMPLO.ymd, i);
    const starts = ["09:45", "10:30", "11:15", "12:00", "12:45", "15:00", "15:45", "16:30", "17:15", "18:00", "18:45", "19:30"];
    starts.forEach((s, k) => {
      if ((i * 5 + k) % 3 === 0) otras.push({ practica_id: `o${i}_${k}`, instructor_id: "p0", alumno_id: `otro_${k}`, vehicle_id: "c1", ymd, start: s, end: hhmm(toMin(s) + 45), type: "ciudad", status: "programada" });
    });
  }
  const propias = origen === "asignada" ? suyas.slice(0, 1) : suyas;
  return {
    nombres: { p0: nombre },
    schedule: { schedule: HORARIO_CAMBIO },
    alumno_id: "al_pablo",
    propias,
    // Las suyas no se pisan con las de los demás (el patrón se salta sus horas).
    practicas: [...suyas, ...otras.filter((o) => !suyas.some((s) => s.ymd === o.ymd && toMin(s.start) < toMin(o.end) && toMin(o.start) < toMin(s.end)))],
  };
}

const absMin = (ymd, min) => {
  const [y, m, d] = ymd.split("-").map(Number);
  const [ya, ma, da] = AHORA_EJEMPLO.ymd.split("-").map(Number);
  return (Date.UTC(y, m - 1, d) - Date.UTC(ya, ma - 1, da)) / 60000 + min;
};
const AHORA_MIN = absMin(AHORA_EJEMPLO.ymd, toMin(AHORA_EJEMPLO.hhmm));
const solapa = (a1, a2, b1, b2) => a1 < b2 && b1 < a2;
const duracion = (p) => toMin(p.end) - toMin(p.start);

function rangos(ymd) {
  if (esFestivoNacional(ymd)) return [];
  return HORARIO_CAMBIO[DIAS_ES[weekday(ymd)]] || [];
}

/** moverSlots.practicasMovibles */
export function practicasMovibles({ ej, cfg: rawCfg }) {
  const cfg = semanaCambioConfig(rawCfg);
  const limite = AHORA_MIN + cfg.antelacion_horas * 60;
  return ej.propias.filter((p) => p.status === "programada" && absMin(p.ymd, toMin(p.start)) >= limite);
}

/** moverSlots.huecosParaMoverDia: [{ ymd, hhmm, fin }] */
export function huecosDia({ ymd, practica, ej, cfg: rawCfg }) {
  const cfg = semanaCambioConfig(rawCfg);
  const dur = duracion(practica);
  const minStart = AHORA_MIN + cfg.antelacion_horas * 60;
  const otras = ej.practicas.filter((p) => p.status !== "cancelada" && p.practica_id !== practica.practica_id);
  const iv = (pred) => otras.filter(pred).map((p) => [absMin(p.ymd, toMin(p.start)), absMin(p.ymd, toMin(p.end))]);
  const delProfesor = iv((p) => p.instructor_id === practica.instructor_id);
  const ocupado = [...delProfesor, ...iv((p) => p.alumno_id === ej.alumno_id), ...(practica.vehicle_id ? iv((p) => p.vehicle_id === practica.vehicle_id) : [])];
  const tramos = rangos(ymd).map((r) => [absMin(ymd, toMin(r.start)), absMin(ymd, toMin(r.end))]);
  const cand = new Set();
  for (const [rs, re] of tramos) {
    for (let s = rs; s + dur <= re; s += dur) cand.add(s);
    if (re - dur >= rs) cand.add(re - dur);
  }
  for (const [bs, be] of delProfesor) { cand.add(be); cand.add(bs - dur); }
  const antes = absMin(practica.ymd, toMin(practica.start));
  const dia0 = absMin(ymd, 0);
  const out = [];
  for (const s of [...cand].sort((a, b) => a - b)) {
    const e = s + dur;
    if (s < minStart || s === antes) continue;
    if (!tramos.some(([rs, re]) => s >= rs && e <= re)) continue;
    if (s < dia0 || s >= dia0 + 1440) continue;
    if (ocupado.some(([bs, be]) => solapa(s, e, bs, be))) continue;
    out.push({ ymd, hhmm: hhmm(s - dia0), fin: hhmm(e - dia0) });
    if (out.length >= 100) break;
  }
  return out;
}

/** moverSlots.diasParaMover: [{ ymd, label, huecos }] */
export function diasLibres({ practica, ej, cfg: rawCfg }) {
  const cfg = semanaCambioConfig(rawCfg);
  const out = [];
  for (let i = 0; i <= cfg.dias_vista; i++) {
    const ymd = addDays(AHORA_EJEMPLO.ymd, i);
    const h = huecosDia({ ymd, practica, ej, cfg });
    if (h.length) out.push({ ymd, label: dayLabel(ymd), huecos: h.length });
  }
  return out;
}

const TIPOS = { ciudad: "Ciudad", carretera: "Carretera", autovia: "Autovía", examen: "Examen", por_definir: "Práctica" };
/** «el martes 6 de octubre a las 10:30» (moverSlots.cuandoTexto). */
export function cuandoTexto(ymd, h) {
  const dia = dayLabel(ymd);
  return `el ${dia.charAt(0).toLowerCase()}${dia.slice(1)} a las ${h}`;
}

/** Opciones de M_PRACTICA (moverFlow.etiquetaCorta). */
export function opcionesPracticas(movibles, nombres) {
  return movibles.map((p) => {
    const [nombreDia, num] = dayLabel(p.ymd).split(" ");
    const prof = firstName(nombres[p.instructor_id]);
    return { id: p.practica_id, title: `${nombreDia} ${num}, ${p.start}`.slice(0, 30), description: `${TIPOS[p.type] || "Práctica"}${prof ? ` con ${prof}` : ""}` };
  });
}

/** moverFlow.resumenCambio */
export function resumenCambio(practica, nuevo, nombres) {
  const prof = firstName(nombres[practica.instructor_id]);
  return `Tu práctica ${cuandoTexto(practica.ymd, practica.start).replace(/^el /, "del ")} pasa ${cuandoTexto(nuevo.ymd, nuevo.hhmm).replace(/^el /, "al ")}${prof ? ` con ${prof}` : ""}.`;
}

/** Pantalla M_FECHA (moverFlow.pantallaFecha): límites del calendario, días sin horas y, con un día, sus horas. */
export function pantallaFechaCambio({ practica, ej, cfg, ymd = null }) {
  const dias = diasLibres({ practica, ej, cfg });
  if (!dias.length) return { aviso: { titulo: CAMBIO_TEXTOS.sinHorasTitulo, texto: CAMBIO_TEXTOS.sinHoras } };
  const libres = new Set(dias.map((d) => d.ymd));
  const min = dias[0].ymd;
  const max = dias[dias.length - 1].ymd;
  const noDisponibles = [];
  for (let d = min; d <= max; d = addDays(d, 1)) if (!libres.has(d)) noDisponibles.push(d);
  const slots = ymd && libres.has(ymd) ? huecosDia({ ymd, practica, ej, cfg }) : [];
  const prof = firstName(ej.nombres[practica.instructor_id]);
  const cabecera = `Tu práctica ${cuandoTexto(practica.ymd, practica.start)}${prof ? ` con ${prof}` : ""}.`;
  return {
    min_date: min,
    max_date: max,
    unavailable_dates: noDisponibles,
    mensaje: slots.length ? `${dayLabel(ymd)}. Elige la hora.` : `${cabecera} Toca un día para ver sus horas libres.`,
    horas: slots.map((s) => ({ id: `${s.ymd}T${s.hhmm}`, title: s.hhmm, slot: s })),
    is_time_enabled: slots.length > 0,
  };
}
